import {
  addCourtDate,
  removeCourtDate,
  useCreateLegalCase,
  useDeleteLegalCase,
  useListLegalCases,
  useUpdateLegalCase,
  type CourtDateKind,
  type LegalCaseIn,
  type LegalCaseOut,
} from '@myrentalmate/api-client';
import { Alert, Button, Chip, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { DataTable } from '../../components/DataTable';
import { FormDialog } from '../../components/FormDialog';
import {
  EMAIL_FIELD,
  optional,
  PHONE_FIELD,
  RecordPage,
  required,
  type Column,
  type Field,
  type FormValues,
  type ListParams,
} from '../../components/RecordPage';
import { apiErrorMessage } from '../../lib/apiError';
import { listResult } from '../../lib/apiData';
import { formatPhone } from '../../lib/contact';
import { formatDate, formatMoney, todayIso } from '../../lib/format';
import { useCan } from '../../lib/permissions';
import { optionsOf } from '../maintenance/labels';
import { RecordExtras } from '../records/RecordExtras';
import { CASE_STATUS, COURT_KIND } from './labels';
import { useLeaseOptions } from './useLeaseOptions';

const COURT_FIELDS: Field[] = [
  {
    name: 'kind',
    label: 'Type',
    type: 'select',
    required: true,
    defaultValue: 'hearing',
    options: optionsOf(COURT_KIND),
  },
  { name: 'occurs_on', label: 'Date', type: 'date', required: true },
  { name: 'time_text', label: 'Time', helperText: 'As the court gave it, like 9:30 AM' },
  { name: 'location', label: 'Location' },
  { name: 'notes', label: 'Notes', type: 'multiline' },
];

const COLUMNS: Column<LegalCaseOut>[] = [
  { label: 'Rental', render: (c) => c.place },
  { label: 'Residents', render: (c) => c.residents || '–' },
  { label: 'Case Number', render: (c) => c.case_number ?? '–' },
  { label: 'Attorney', render: (c) => c.attorney_name ?? '–' },
  {
    label: 'Next Court Date',
    render: (c) => (c.next_court_date ? formatDate(c.next_court_date) : '–'),
  },
  { label: 'Owed', render: (c) => formatMoney(c.balance) },
  {
    label: 'Status',
    render: (c) => (
      <Chip size="small" color={CASE_STATUS[c.status].color} label={CASE_STATUS[c.status].label} />
    ),
  },
];

function CourtDates({ legalCase, canManage }: { legalCase: LegalCaseOut; canManage: boolean }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [dates, setDates] = useState(legalCase.court_dates);
  const [error, setError] = useState<string | null>(null);
  return (
    <Stack spacing={1} sx={{ mb: 3 }}>
      <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Court Dates</Typography>
        {canManage && (
          <Button size="small" variant="outlined" onClick={() => setAdding(true)}>
            Add Court Date
          </Button>
        )}
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {dates.length === 0 ? (
        <Typography color="text.secondary">No court dates yet.</Typography>
      ) : (
        <DataTable
          rows={dates}
          rowKey={(d) => d.id}
          columns={[
            { label: 'Date', render: (d) => formatDate(d.occurs_on) },
            { label: 'Time', render: (d) => d.time_text ?? '–' },
            { label: 'Type', render: (d) => COURT_KIND[d.kind] },
            { label: 'Location', render: (d) => d.location ?? '–' },
            {
              label: 'Actions',
              align: 'right',
              render: (d) =>
                canManage && (
                  <Button
                    size="small"
                    color="error"
                    onClick={() => {
                      setError(null);
                      removeCourtDate(legalCase.id, d.id)
                        .then(() => {
                          setDates((rows) => rows.filter((row) => row.id !== d.id));
                          return queryClient.invalidateQueries();
                        })
                        .catch((e: unknown) => setError(apiErrorMessage(e)));
                    }}
                  >
                    Remove
                  </Button>
                ),
            },
          ]}
        />
      )}
      {adding && (
        <FormDialog
          title="Add Court Date"
          fields={COURT_FIELDS}
          initial={{
            kind: 'hearing',
            occurs_on: todayIso(),
            time_text: '',
            location: '',
            notes: '',
          }}
          onSubmit={async (values) => {
            const answer = await addCourtDate(legalCase.id, {
              kind: required(values, 'kind') as CourtDateKind,
              occurs_on: required(values, 'occurs_on'),
              time_text: optional(values, 'time_text'),
              location: optional(values, 'location'),
              notes: optional(values, 'notes'),
            });
            if (answer.status === 200) setDates(answer.data.court_dates);
          }}
          onClose={() => setAdding(false)}
        />
      )}
    </Stack>
  );
}

function toBody(values: FormValues): LegalCaseIn {
  return {
    lease_id: Number(required(values, 'lease_id')),
    status: required(values, 'status') as LegalCaseIn['status'],
    case_number: optional(values, 'case_number'),
    court_name: optional(values, 'court_name'),
    attorney_name: optional(values, 'attorney_name'),
    attorney_phone: optional(values, 'phone'),
    attorney_email: optional(values, 'email'),
    amount_claimed: optional(values, 'amount_claimed'),
    filed_on: optional(values, 'filed_on'),
    judgment_on: optional(values, 'judgment_on'),
    judgment_amount: optional(values, 'judgment_amount'),
    outcome: optional(values, 'outcome'),
  };
}

export function LegalCasesPage() {
  const canManage = useCan('legal.manage');
  const create = useCreateLegalCase();
  const update = useUpdateLegalCase();
  const remove = useDeleteLegalCase();
  const leases = useLeaseOptions();
  const [openOnly, setOpenOnly] = useState(true);
  const useRows = ({ q, page, pageSize }: ListParams) =>
    listResult(useListLegalCases({ q, page, page_size: pageSize, open_only: openOnly }));
  const fields: Field[] = [
    { name: 'lease_id', label: 'Lease', type: 'select', required: true, options: leases },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      defaultValue: 'notice_served',
      options: optionsOf(CASE_STATUS),
    },
    { name: 'case_number', label: 'Case Number' },
    { name: 'court_name', label: 'Court' },
    { name: 'attorney_name', label: 'Attorney' },
    { ...PHONE_FIELD, label: 'Attorney Phone' },
    { ...EMAIL_FIELD, label: 'Attorney Email' },
    { name: 'amount_claimed', label: 'Amount Claimed', type: 'number' },
    { name: 'filed_on', label: 'Date Filed', type: 'date' },
    { name: 'judgment_on', label: 'Judgment Date', type: 'date' },
    { name: 'judgment_amount', label: 'Judgment Amount', type: 'number' },
    { name: 'outcome', label: 'Outcome', type: 'multiline' },
  ];
  return (
    <RecordPage
      title="Eviction Cases"
      singular="case"
      description="A record for you and your attorney. Residents never see these, and nothing here files with a court."
      summary={
        <FormControlLabel
          label="Open Cases Only"
          control={<Switch checked={openOnly} onChange={(e) => setOpenOnly(e.target.checked)} />}
        />
      }
      fields={fields}
      columns={COLUMNS}
      canManage={canManage}
      useRows={useRows}
      toValues={(c) => ({
        lease_id: String(c.lease_id),
        status: c.status,
        case_number: c.case_number ?? '',
        court_name: c.court_name ?? '',
        attorney_name: c.attorney_name ?? '',
        phone: formatPhone(c.attorney_phone),
        email: c.attorney_email ?? '',
        amount_claimed: c.amount_claimed ?? '',
        filed_on: c.filed_on ?? '',
        judgment_on: c.judgment_on ?? '',
        judgment_amount: c.judgment_amount ?? '',
        outcome: c.outcome ?? '',
      })}
      create={(values) => create.mutateAsync({ data: toBody(values) })}
      update={(recordId, values) => update.mutateAsync({ caseId: recordId, data: toBody(values) })}
      remove={(recordId) => remove.mutateAsync({ caseId: recordId })}
      renderExtras={(row) => (
        <>
          <CourtDates legalCase={row} canManage={canManage} />
          <RecordExtras entityType="legal_case" entityId={row.id} canManage={canManage} />
        </>
      )}
    />
  );
}
